import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import axios from "axios";
import { FaArrowLeft, FaCheckCircle, FaStore, FaTruck } from "react-icons/fa";
import Logo from "../../assets/logo/new_logo_colored2.png";
import { useCart } from "../../subjects/CartContext";
import { thousands_separators } from "../../utils/formatCurrency";
import { formatPhoneNumber } from "../../utils/formatPhonenumber";
import { getCurrentWarehouse } from "../../utils/getCurrentWarehouse";
import { BASE_API_URL } from "../../utils/app-constants";
import { ACCENT, TEAL, INK, MUTED, LINE } from "../products/Products";

// NOTE: assumes your backend's walk-in/guest-checkout resource for Express
// lives at this path (mirrors PublicOrdersResource) — point this at whatever
// it's actually called if different.
const EXPRESS_ORDER_ENDPOINT = `${BASE_API_URL}public/markitiexpress/order`;

const isSunday = (dateString) => new Date(dateString).getDay() === 0;

// Same slugging rule Express.jsx uses to match /express/:hub, kept in sync
// here so the "back to your branch" link after checkout lands on the same
// URL the customer arrived from.
const slugify = (name) => (name || "").toLowerCase().trim().replace(/\s+/g, "-");

const ExpressCheckout = () => {
  const history = useHistory();
  const { items, setItems, getTotalPrice, getItemsCount, isSelfCollection, setIsSelfCollection } = useCart();
  const currentWarehouse = getCurrentWarehouse();
  const [order, setOrder] = useState(null);
  const [phase, setPhase] = useState("details"); // details -> payment -> instructions

  const [selfCollect, setSelfCollect] = useState(true);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { delivery_time: "Late Morning" } });

  useEffect(() => {
    setIsSelfCollection(selfCollect);
  }, [selfCollect, setIsSelfCollection]);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const placeOrder = (payload) => axios.post(EXPRESS_ORDER_ENDPOINT, payload);
  const { mutate: submitOrder, isLoading: placing } = useMutation(placeOrder, {
    onSuccess: (res) => {
      setOrder(res.data?.returned_resultset?.[0] || res.data);
      setPhase("payment");
    },
    onError: (err) => toast.error(err?.response?.data?.message || "Couldn't place the order — try again"),
  });

  const onSubmit = (data) => {
    if (!items.length) return toast.error("Cart is empty");
    const payload = {
      cart_items: items.map((item) => ({
        product_id: item.product.id,
        quantity: item.qty,
        cooking_method: item.cooking_method || "none",
      })),
      warehouse_id: currentWarehouse?.id,
      self_collect: selfCollect,
      // Optional by design — a walk-in shouldn't have to hand over contact
      // details just to check out. Phone is asked again, only if needed,
      // at the M-Pesa step.
      first_name: data.first_name || undefined,
      phone_number: data.phone_number ? formatPhoneNumber(data.phone_number) : undefined,
      notes: data.notes || "",
      notification_consent: false,
      order_source: "markiti_express",
      address: selfCollect ? `Self Collection - ${currentWarehouse?.name || ""}` : data.address,
      building: selfCollect ? currentWarehouse?.name : data.building,
    };
    if (!selfCollect) payload.delivery_time = `${data.date}, ${data.delivery_time}`;
    submitOrder(payload);
  };

  // Carried through to /thank-you so it can route the customer back to
  // *this* branch's Express page instead of the general homepage.
  const branchSlug = slugify(currentWarehouse?.name);
  const thankYouParams = (type) =>
    `order_number=${order?.order_number}&type=${type}&source=express${
      branchSlug ? `&branch=${branchSlug}` : ""
    }`;

  const { register: payRegister, handleSubmit: paySubmit } = useForm();
  const handleStk = (data) =>
    axios.post(`${BASE_API_URL}public/order/${order.id}/stk`, {
      phonenumber: formatPhoneNumber(data.stk_phone_number),
      amount: getTotalPrice(),
    });
  const { mutate: stk, isLoading: paying } = useMutation(handleStk, {
    onSuccess: () => {
      toast.success("M-Pesa prompt sent — check the customer's phone");
      setItems([]);
      history.push(`/thank-you?${thankYouParams("stk")}`);
    },
    onError: (err) => toast.error(err?.response?.data?.message || "STK push failed — try the paybill instead"),
  });

  const handlePayLater = () => {
    axios
      .put(`${BASE_API_URL}public/order/${order.id}`, { payment_mode: "ondelivery" })
      .catch(() => {})
      .finally(() => setPhase("instructions"));
  };

  if (!items.length && phase === "details") {
    return (
      <Page>
        <Hero>
          <LogoBadge><img src={Logo} alt="Markiti" /></LogoBadge>
          <HeroTitle>Markiti Express</HeroTitle>
        </Hero>
        <Body>
          <Card as="div" style={{ textAlign: "center" }}>
            <p style={{ color: MUTED }}>Your cart is empty.</p>
            <PrimaryBtn onClick={() => history.push("/express")}>Back to products</PrimaryBtn>
          </Card>
        </Body>
      </Page>
    );
  }

  if (phase === "payment") {
    return (
      <Page>
        <Hero>
          <LogoBadge><img src={Logo} alt="Markiti" /></LogoBadge>
          <HeroTitle>Order {order?.order_number}</HeroTitle>
          <HeroTag>KSh {thousands_separators(getTotalPrice())} total</HeroTag>
        </Hero>
        <Body>
          <Card>
            <FieldLabel>Phone number for M-Pesa</FieldLabel>
            <StyledInput placeholder="07XX XXX XXX" {...payRegister("stk_phone_number", { required: true })} />
            <PrimaryBtn disabled={paying} onClick={paySubmit((d) => stk(d))}>
              {paying ? "Sending..." : "Pay Now (M-Pesa)"}
            </PrimaryBtn>
            <GhostBtn onClick={handlePayLater}>
              Pay on {isSelfCollection ? "Collection" : "Delivery"}
            </GhostBtn>
          </Card>
        </Body>
      </Page>
    );
  }

  if (phase === "instructions") {
    return (
      <Page>
        <SuccessHero>
          <FaCheckCircle size={46} />
          <HeroTitle>Order confirmed</HeroTitle>
          <HeroTag>{order?.order_number}</HeroTag>
        </SuccessHero>
        <Body>
          <Card>
            <p style={{ color: INK }}>Pay via M-Pesa Paybill whenever ready:</p>
            {/*
              Business Number is still a shared constant. To make this vary
              per branch, add a paybill field (e.g. mpesa_paybill) to the
              warehouse record returned by /public/warehouse and it'll be
              picked up automatically — falls back to the shared number
              until that field exists on the backend.
            */}
            <SummaryRow><span>Business Number</span><strong>{currentWarehouse?.mpesa_paybill || "4015545"}</strong></SummaryRow>
            <SummaryRow><span>Account Number</span><strong>{order?.order_number}</strong></SummaryRow>
            <SummaryRow><span>Amount</span><strong>KSh {thousands_separators(getTotalPrice())}</strong></SummaryRow>
            <PrimaryBtn
              onClick={() => {
                setItems([]);
                history.push(`/thank-you?${thankYouParams("paybill")}`);
              }}
            >
              Done
            </PrimaryBtn>
          </Card>
        </Body>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        <TopRow>
          <BackChevron onClick={() => history.push("/express")}><FaArrowLeft size={14} /></BackChevron>
          <LogoBadge><img src={Logo} alt="Markiti" /></LogoBadge>
          <span />
        </TopRow>
        <HeroTitle>Checkout</HeroTitle>
        <HeroTag>{getItemsCount()} item{getItemsCount() === 1 ? "" : "s"} · KSh {thousands_separators(getTotalPrice())}</HeroTag>
      </Hero>

      <Body as="form" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <SectionLabel>How would you like this?</SectionLabel>
          <ModeRow>
            <ModeOption as="button" type="button" $active={selfCollect} onClick={() => setSelfCollect(true)}>
              <FaStore size={16} />
              Self Collection
            </ModeOption>
            <ModeOption as="button" type="button" $active={!selfCollect} onClick={() => setSelfCollect(false)}>
              <FaTruck size={16} />
              Delivery
            </ModeOption>
          </ModeRow>

          {!selfCollect && (
            <>
              <FieldLabel>Address *</FieldLabel>
              <StyledInput {...register("address", { required: !selfCollect })} />
              {errors.address && <ErrorMsg>Address is required for delivery</ErrorMsg>}
              <FieldLabel>Building *</FieldLabel>
              <StyledInput {...register("building", { required: !selfCollect })} />
              {errors.building && <ErrorMsg>Building is required for delivery</ErrorMsg>}
              <FieldLabel>Delivery date *</FieldLabel>
              <StyledInput
                type="date"
                min={tomorrow.toISOString().split("T")[0]}
                {...register("date", {
                  required: !selfCollect,
                  validate: (v) => selfCollect || !isSunday(v) || "No deliveries on Sundays",
                })}
              />
              {errors.date && <ErrorMsg>{errors.date.message || "Delivery date is required"}</ErrorMsg>}
            </>
          )}
        </Card>

        <Card>
          <SectionLabel>Your details <OptionalTag>optional</OptionalTag></SectionLabel>
          <FieldLabel>Name</FieldLabel>
          <StyledInput placeholder="First and last name" {...register("first_name")} />
          <FieldLabel>Phone</FieldLabel>
          <StyledInput placeholder="07XX XXX XXX — speeds up M-Pesa" {...register("phone_number")} />
          <FieldLabel>Notes</FieldLabel>
          <StyledTextArea {...register("notes")} />
        </Card>

        <PrimaryBtn type="submit" disabled={placing}>
          {placing ? "Placing order..." : "Continue to Payment"}
        </PrimaryBtn>
      </Body>
    </Page>
  );
};

export default ExpressCheckout;

const Page = styled.div`
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  background: #f6f9fc;
  padding-bottom: 2rem;
`;
const Hero = styled.div`
  background: linear-gradient(135deg, ${ACCENT} 0%, #2e2a63 100%);
  border-radius: 0 0 1.75rem 1.75rem;
  padding: 1.1rem 1.25rem 1.5rem;
  color: #fff;
`;
const SuccessHero = styled(Hero)`
  background: linear-gradient(135deg, ${TEAL} 0%, #1c8b83 100%);
  text-align: center;
  padding-top: 2rem;
`;
const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const BackChevron = styled.button`
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const LogoBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  img {
    width: 100%;
    object-fit: contain;
  }
`;
const HeroTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0.9rem 0 0.1rem;
`;
const HeroTag = styled.p`
  margin: 0;
  font-size: 0.82rem;
  opacity: 0.85;
`;
const Body = styled.div`
  padding: 1rem 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;
const Card = styled.div`
  background: #fff;
  border-radius: 1.1rem;
  padding: 1.1rem;
  box-shadow: 0 6px 24px rgba(36, 28, 64, 0.06);
`;
const SectionLabel = styled.div`
  font-weight: 800;
  color: ${INK};
  font-size: 0.9rem;
  margin-bottom: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;
const OptionalTag = styled.span`
  font-weight: 700;
  font-size: 0.65rem;
  color: ${MUTED};
  background: #f1f0f8;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
`;
const ModeRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
`;
const ModeOption = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.8rem;
  border-radius: 0.75rem;
  border: 2px solid ${(p) => (p.$active ? ACCENT : LINE)};
  background: ${(p) => (p.$active ? "#F6F5FC" : "#fff")};
  color: ${(p) => (p.$active ? ACCENT : MUTED)};
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
`;
const FieldLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${MUTED};
  margin: 0.7rem 0 0.35rem;
`;
const StyledInput = styled.input`
  width: 100%;
  padding: 0.8rem 0.9rem;
  border-radius: 0.6rem;
  border: 1.5px solid ${LINE};
  font-size: 0.9rem;
  &:focus {
    outline: none;
    border-color: ${ACCENT};
  }
`;
const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 0.8rem 0.9rem;
  border-radius: 0.6rem;
  border: 1.5px solid ${LINE};
  font-size: 0.9rem;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${ACCENT};
  }
`;
const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.3rem;
`;
const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-top: 1px solid ${LINE};
  font-size: 0.88rem;
  color: ${INK};
`;
const PrimaryBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, ${ACCENT} 0%, #2e2a63 100%);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem;
  font-weight: 800;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 0.9rem;
  box-shadow: 0 8px 24px rgba(73, 67, 149, 0.25);
  &:disabled {
    opacity: 0.6;
  }
`;
const GhostBtn = styled.button`
  width: 100%;
  background: #f1f5f9;
  color: ${INK};
  border: none;
  border-radius: 0.85rem;
  padding: 1rem;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 0.6rem;
`;